import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Eye, EyeOff, Handshake, Trash2, X } from 'lucide-react';
import { AdminSidebar } from '../../components/AdminSidebar';
import { InvitationPreview } from '../../components/InvitationPreview';
import { assetUrl } from '../../components/design/DesignLayers';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { API_URL } from '../../utils/api';
import { loadFontStylesheets } from '../../utils/fonts';
import { formatDate } from '../../utils/format';
import { getErrorMessage } from '../../utils/errors';
import { useDocumentTitle } from '../../hooks/use-document-title';
import { money, StatusPill } from '../partner/partnerStatus';

const ACCOUNT_STATUS = {
  pending: 'bg-[#E8F0FE] text-[#1A4FB0]',
  approved: 'bg-[#E3F4E8] text-[#1F6B3A]',
  rejected: 'bg-[#FDE8E8] text-[#9B1C1C]',
  suspended: 'bg-[#EFEBE3] text-[#5C574D]',
};
const AccountPill = ({ status }) => (
  <span className={`rounded-full px-2.5 py-1 text-[11px] font-extrabold capitalize ${ACCOUNT_STATUS[status] || ''}`}>{status}</span>
);

const fieldText = (f) => [f.prefix, f.default_text, f.suffix].filter(Boolean).join('');

// ───────────────────────── template review ─────────────────────────

const ReviewDialog = ({ templateId, onClose, onDone }) => {
  const [t, setT] = useState(null);
  const [form, setForm] = useState({ price: '', template_code: '', background_code: '', note: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!templateId) return;
    setT(null);
    axios.get(`${API_URL}/admin/partner-templates/${templateId}`).then(({ data }) => {
      setT(data);
      loadFontStylesheets(data.fonts || []);
      setForm({
        price: data.live?.price ?? data.suggested_price ?? '',
        template_code: data.live?.template_code || '',
        background_code: data.live?.background_code || '',
        note: '',
      });
    });
  }, [templateId]);

  const act = async (kind) => {
    setBusy(true);
    try {
      if (kind === 'approve') {
        await axios.post(`${API_URL}/admin/partner-templates/${templateId}/approve`, {
          price: form.price === '' ? null : Number(form.price),
          template_code: form.template_code || null,
          background_code: form.background_code || null,
          note: form.note,
        });
        toast.success('Published to the catalog');
      } else {
        if (!form.note.trim()) {
          toast.error('Write what the partner should change');
          setBusy(false);
          return;
        }
        await axios.post(`${API_URL}/admin/partner-templates/${templateId}/reject`, { note: form.note });
        toast.success('Sent back to the partner');
      }
      onDone();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(templateId)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t ? t.name : 'Loading…'}</DialogTitle>
          <DialogDescription>{t ? `${t.partner?.company_name || ''} · submitted ${formatDate(t.submitted_at)}` : ''}</DialogDescription>
        </DialogHeader>
        {t ? (
          <div className="grid md:grid-cols-[1fr_320px] gap-6">
            <div className="rounded border border-ink/10 overflow-hidden">
              <InvitationPreview backgroundImage={assetUrl(t.background_image)} fields={t.text_fields} getText={fieldText} eager />
            </div>
            <div className="grid gap-4 content-start">
              <div className="text-sm">
                <p className="font-bold mb-1">Text fields</p>
                <ul className="grid gap-1">
                  {t.text_fields.map((f) => (
                    <li key={f.id} className="flex justify-between gap-2">
                      <span className="truncate">{f.label}</span>
                      <span className="text-ink/50 shrink-0">{f.field_input_id ? 'category' : f.static ? 'fixed' : 'editable'}</span>
                    </li>
                  ))}
                </ul>
                {t.fonts?.length ? <p className="text-ink/60 mt-2">Partner fonts: {t.fonts.map((f) => f.name).join(', ')}</p> : null}
                {t.partner_note ? <p className="mt-2"><span className="font-bold">Partner note:</span> {t.partner_note}</p> : null}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="grid gap-1">
                  <Label htmlFor="rv-price">Price $</Label>
                  <Input id="rv-price" type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="rv-code">Code</Label>
                  <Input id="rv-code" inputMode="numeric" maxLength={4} placeholder="4 digits" value={form.template_code} onChange={(e) => setForm({ ...form, template_code: e.target.value.replace(/\D/g, '') })} />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="rv-bcode">Bg code</Label>
                  <Input id="rv-bcode" inputMode="numeric" maxLength={3} placeholder="3 digits" value={form.background_code} onChange={(e) => setForm({ ...form, background_code: e.target.value.replace(/\D/g, '') })} />
                </div>
              </div>
              <p className="text-xs text-ink/50 -mt-2">
                Phone settings come from the category. Set the 4-digit code here so callers can order it by number; anything else can be fine-tuned later on the Templates page.
              </p>
              <div className="grid gap-1">
                <Label htmlFor="rv-note">Note to the partner</Label>
                <Textarea id="rv-note" rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Required when sending it back" />
              </div>
            </div>
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" disabled={busy || !t} onClick={() => act('reject')}>
            <X className="h-4 w-4" /> Send back
          </Button>
          <Button disabled={busy || !t} onClick={() => act('approve')} className="bg-ink text-paper hover:bg-ink/90">
            <Check className="h-4 w-4" /> {t?.live_template_id ? 'Approve changes' : 'Approve & publish'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TemplatesTable = ({ status, onReview, refreshKey, onChanged }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    setRows(null);
    axios
      .get(`${API_URL}/admin/partner-templates${status ? `?status=${status}` : ''}`)
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]));
  }, [status, refreshKey]);

  const visibility = async (row, active) => {
    try {
      await axios.post(`${API_URL}/admin/partner-templates/${row.id}/visibility`, { active });
      onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not change visibility'));
    }
  };

  if (rows === null) return <p className="text-ink/60">Loading…</p>;
  if (!rows.length) return <p className="text-ink/60">{status === 'pending' ? 'Nothing waiting for review.' : 'No partner templates yet.'}</p>;
  return (
    <div className="overflow-x-auto rounded-xl border border-ink/10 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-cream text-left">
          <tr>
            <th className="px-3 py-2 w-16" />
            <th className="px-3 py-2">Template</th>
            <th className="px-3 py-2">Partner</th>
            <th className="px-3 py-2">Category</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Code</th>
            <th className="px-3 py-2 text-right">Sales</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-ink/5">
              <td className="px-3 py-2">
                {r.background_image ? <img src={assetUrl(r.background_image)} alt="" className="w-10 aspect-[8.5/11] object-cover rounded-sm" loading="lazy" /> : null}
              </td>
              <td className="px-3 py-2 font-bold">
                {r.name}
                {r.live_template_id && r.has_unpublished_changes ? <span className="block text-xs font-normal text-ink/50">has unpublished edits</span> : null}
              </td>
              <td className="px-3 py-2">{r.partner_name}</td>
              <td className="px-3 py-2">{r.category_name}</td>
              <td className="px-3 py-2"><StatusPill status={r.status} live={r.live ? r.live.is_active : undefined} /></td>
              <td className="px-3 py-2">{r.live?.template_code || '—'}</td>
              <td className="px-3 py-2 text-right">{r.sales.count} · {money(r.sales.gross)}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <Button size="sm" variant={r.status === 'pending' ? 'default' : 'outline'} onClick={() => onReview(r.id)}>
                  {r.status === 'pending' ? 'Review' : 'Open'}
                </Button>
                {r.live ? (
                  <Button size="sm" variant="outline" className="ml-1.5" onClick={() => visibility(r, !r.live.is_active)} title={r.live.is_active ? 'Hide from catalog' : 'Show in catalog'}>
                    {r.live.is_active ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ───────────────────────── partner accounts ─────────────────────────

const PartnerSheet = ({ partnerId, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [rate, setRate] = useState('');
  const [note, setNote] = useState('');
  const [payout, setPayout] = useState({ amount: '', method: 'Zelle', reference: '', paid_at: '' });

  const apply = (data) => {
    setD(data);
    setRate(data.partner.commission_percent ?? '');
    setNote(data.partner.admin_note || '');
  };
  useEffect(() => {
    if (!partnerId) return;
    setD(null);
    axios.get(`${API_URL}/admin/partners/${partnerId}`).then(({ data }) => apply(data));
  }, [partnerId]);

  const update = async (body, msg) => {
    try {
      const { data } = await axios.put(`${API_URL}/admin/partners/${partnerId}`, body);
      apply(data);
      if (msg) toast.success(msg);
      onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not save'));
    }
  };

  const addPayout = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API_URL}/admin/partners/${partnerId}/payouts`, {
        ...payout,
        amount: Number(payout.amount),
        paid_at: payout.paid_at || null,
      });
      apply(data);
      setPayout({ amount: '', method: payout.method, reference: '', paid_at: '' });
      toast.success('Payment recorded');
      onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not record the payment'));
    }
  };

  const removePayout = async (id) => {
    if (!window.confirm('Delete this payment record?')) return;
    const { data } = await axios.delete(`${API_URL}/admin/partners/${partnerId}/payouts/${id}`);
    apply(data);
    onChanged();
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const { data } = await axios.post(`${API_URL}/admin/partners/${partnerId}/logo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      apply(data);
      toast.success('Logo saved');
      onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Upload failed'));
    }
  };

  const removeLogo = async () => {
    try {
      const { data } = await axios.delete(`${API_URL}/admin/partners/${partnerId}/logo`);
      apply(data);
      onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not remove the logo'));
    }
  };

  const p = d?.partner;
  return (
    <Sheet open={Boolean(partnerId)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{p?.company_name || 'Loading…'}</SheetTitle>
          <SheetDescription>{p ? [p.email, p.website, p.phone].filter(Boolean).join(' · ') : ''}</SheetDescription>
        </SheetHeader>
        {p ? (
          <div className="grid gap-6 mt-6 text-sm">
            <section className="grid gap-2">
              <div className="flex items-center gap-2">
                <AccountPill status={p.status} />
                <span className="text-ink/50">applied {formatDate(p.created_at)}</span>
              </div>
              {p.notes ? <p className="text-ink/70 whitespace-pre-wrap">{p.notes}</p> : null}
              <div className="flex flex-wrap gap-2">
                {p.status !== 'approved' ? (
                  <Button size="sm" onClick={() => update({ status: 'approved' }, 'Account approved')} className="bg-ink text-paper">Approve</Button>
                ) : null}
                {p.status === 'pending' ? <Button size="sm" variant="outline" onClick={() => update({ status: 'rejected' }, 'Application rejected')}>Reject</Button> : null}
                {p.status === 'approved' ? <Button size="sm" variant="outline" onClick={() => update({ status: 'suspended' }, 'Account suspended, templates hidden')}>Suspend</Button> : null}
              </div>
            </section>

            <section className="grid gap-2">
              <h3 className="font-bold">Logo</h3>
              <p className="text-ink/60">The tag on this partner&rsquo;s templates in the gallery. The partner can also set it in their portal.</p>
              <div className="flex flex-wrap items-center gap-2">
                <div className="h-12 min-w-[110px] px-3 rounded-lg border border-dashed border-ink/15 bg-cream inline-flex items-center justify-center">
                  {p.logo_url ? (
                    <img src={assetUrl(p.logo_url)} alt={`${p.company_name} logo`} className="h-7 w-auto max-w-[200px] object-contain" />
                  ) : (
                    <span className="text-xs text-ink/50">No logo</span>
                  )}
                </div>
                <label className="inline-flex items-center h-9 px-3 rounded-md border border-input bg-background text-sm font-medium cursor-pointer hover:bg-accent">
                  {p.logo_url ? 'Replace' : 'Upload'}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                </label>
                {p.logo_url ? <Button size="sm" variant="outline" onClick={removeLogo}>Remove</Button> : null}
              </div>
            </section>

            <section className="grid gap-2">
              <h3 className="font-bold">Commission</h3>
              <p className="text-ink/60">Azamein keeps this share of every sale. Leave empty to use the default. A new rate applies to new sales only.</p>
              <div className="flex gap-2 items-end">
                <div className="grid gap-1">
                  <Label htmlFor="ps-rate">Rate %</Label>
                  <Input id="ps-rate" type="number" min="0" max="100" step="0.5" className="w-28" value={rate} onChange={(e) => setRate(e.target.value)} placeholder={`${p.effective_commission_percent}`} />
                </div>
                <Button size="sm" variant="outline" onClick={() => update(rate === '' ? { clear_commission: true } : { commission_percent: Number(rate) }, 'Commission saved')}>
                  Save
                </Button>
                <span className="text-ink/50 pb-2">now {p.effective_commission_percent}%</span>
              </div>
            </section>

            <section className="grid gap-2">
              <h3 className="font-bold">Earnings</h3>
              <div className="grid grid-cols-3 gap-2">
                {[
                  ['Sales', `${d.summary.sales} · ${money(d.summary.gross)}`],
                  ['Commission', money(d.summary.commission)],
                  ['Partner share', money(d.summary.earned)],
                  ['Paid out', money(d.summary.paid_out)],
                  ['Balance due', money(d.summary.balance)],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-lg bg-cream p-2.5">
                    <p className="text-[11px] uppercase tracking-wide text-ink/50 font-bold">{k}</p>
                    <p className="font-bold">{v}</p>
                  </div>
                ))}
              </div>
              {p.payout_details ? <p className="text-ink/70"><span className="font-bold">Pay to:</span> {p.payout_details}</p> : null}
            </section>

            <section className="grid gap-2">
              <h3 className="font-bold">Record a payment</h3>
              <form onSubmit={addPayout} className="grid grid-cols-2 gap-2">
                <Input type="number" min="0.01" step="0.01" required placeholder="Amount" value={payout.amount} onChange={(e) => setPayout({ ...payout, amount: e.target.value })} />
                <Input type="date" value={payout.paid_at} onChange={(e) => setPayout({ ...payout, paid_at: e.target.value })} aria-label="Date paid" />
                <Input placeholder="Method (Zelle, check…)" value={payout.method} onChange={(e) => setPayout({ ...payout, method: e.target.value })} />
                <Input placeholder="Reference" value={payout.reference} onChange={(e) => setPayout({ ...payout, reference: e.target.value })} />
                <Button type="submit" size="sm" className="col-span-2 bg-ink text-paper">Record payment</Button>
              </form>
              {d.payouts.length ? (
                <ul className="grid gap-1 mt-1">
                  {d.payouts.map((x) => (
                    <li key={x.id} className="flex items-center justify-between gap-2 border-b border-ink/5 py-1.5">
                      <span>{formatDate(x.paid_at)} · {x.method} {x.reference ? `· ${x.reference}` : ''}</span>
                      <span className="flex items-center gap-2 font-bold">
                        {money(x.amount)}
                        <button type="button" onClick={() => removePayout(x.id)} aria-label="Delete payment" className="text-ink/40 hover:text-red-700">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>

            <section className="grid gap-2">
              <h3 className="font-bold">Sales</h3>
              {d.sales.length ? (
                <ul className="grid gap-1">
                  {d.sales.map((s) => (
                    <li key={s.id} className="flex justify-between gap-2 border-b border-ink/5 py-1.5">
                      <span className="truncate">{formatDate(s.sold_at)} · {s.template_name}</span>
                      <span className="shrink-0">{money(s.amount)} → {money(s.partner_amount)} <span className="text-ink/40">({s.commission_percent}%)</span></span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-ink/50">No sales yet.</p>
              )}
            </section>

            <section className="grid gap-2">
              <h3 className="font-bold">Admin notes</h3>
              <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
              <Button size="sm" variant="outline" className="justify-self-start" onClick={() => update({ admin_note: note }, 'Notes saved')}>Save notes</Button>
            </section>

            {d.fonts.length ? (
              <section className="grid gap-1">
                <h3 className="font-bold">Fonts ({d.fonts.length})</h3>
                <p className="text-ink/60">{d.fonts.map((f) => f.name).join(', ')}</p>
              </section>
            ) : null}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};

const PartnersTable = ({ rows, onOpen }) => {
  if (!rows.length) return <p className="text-ink/60">No partner accounts yet. Invitation sites apply at /partner.</p>;
  return (
    <div className="overflow-x-auto rounded-xl border border-ink/10 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-cream text-left">
          <tr>
            <th className="px-3 py-2">Partner</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Templates</th>
            <th className="px-3 py-2 text-right">Commission</th>
            <th className="px-3 py-2 text-right">Sales</th>
            <th className="px-3 py-2 text-right">Balance due</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className="border-t border-ink/5">
              <td className="px-3 py-2">
                <p className="font-bold inline-flex items-center gap-2">
                  {p.logo_url ? <img src={assetUrl(p.logo_url)} alt="" className="h-5 w-auto max-w-[80px] object-contain" /> : null}
                  {p.company_name}
                </p>
                <p className="text-xs text-ink/50">{p.email}</p>
              </td>
              <td className="px-3 py-2"><AccountPill status={p.status} /></td>
              <td className="px-3 py-2">
                {p.live_templates} live
                {p.template_counts.pending ? <span className="text-[#1A4FB0]"> · {p.template_counts.pending} to review</span> : null}
              </td>
              <td className="px-3 py-2 text-right">{p.effective_commission_percent}%{p.commission_percent == null ? <span className="text-ink/40"> (default)</span> : null}</td>
              <td className="px-3 py-2 text-right">{p.earnings.sales} · {money(p.earnings.gross)}</td>
              <td className="px-3 py-2 text-right font-bold">{money(p.earnings.balance)}</td>
              <td className="px-3 py-2 text-right">
                <Button size="sm" variant={p.status === 'pending' ? 'default' : 'outline'} onClick={() => onOpen(p.id)}>
                  {p.status === 'pending' ? 'Review' : 'Open'}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ───────────────────────── page ─────────────────────────

export default function AdminPartners() {
  const [data, setData] = useState({ partners: [], settings: null });
  const [rate, setRate] = useState('');
  const [reviewId, setReviewId] = useState(null);
  const [partnerId, setPartnerId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  useDocumentTitle('Partners');

  const load = useCallback(() => {
    axios.get(`${API_URL}/admin/partners`).then(({ data: d }) => {
      setData(d);
      setRate(String(d.settings?.default_commission_percent ?? ''));
    });
  }, []);
  useEffect(load, [load]);

  const refresh = () => {
    load();
    setRefreshKey((k) => k + 1);
  };

  const saveRate = async () => {
    try {
      await axios.put(`${API_URL}/admin/partner-settings`, { default_commission_percent: Number(rate) });
      toast.success('Default commission saved');
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not save'));
    }
  };

  const pendingAccounts = data.partners.filter((p) => p.status === 'pending').length;
  const pendingTemplates = data.partners.reduce((n, p) => n + (p.template_counts?.pending || 0), 0);

  return (
    <div className="min-h-screen bg-paper">
      <AdminSidebar />
      <div className="ml-64 p-8">
        <h1 className="font-serif text-3xl font-bold text-ink mb-2 flex items-center gap-3">
          <Handshake className="h-7 w-7 text-gold" /> Partners
        </h1>
        <p className="text-ink/60 mb-6">Invitation sites that design templates for Azamein. Approve accounts and templates, set the commission, and record payments.</p>

        <Tabs defaultValue={pendingTemplates || !pendingAccounts ? 'review' : 'accounts'}>
          <TabsList className="mb-5">
            <TabsTrigger value="review">To review{pendingTemplates ? ` (${pendingTemplates})` : ''}</TabsTrigger>
            <TabsTrigger value="accounts">Accounts{pendingAccounts ? ` (${pendingAccounts} new)` : ''}</TabsTrigger>
            <TabsTrigger value="templates">All templates</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="review">
            <TemplatesTable status="pending" onReview={setReviewId} refreshKey={refreshKey} onChanged={refresh} />
          </TabsContent>
          <TabsContent value="accounts">
            <PartnersTable rows={data.partners} onOpen={setPartnerId} />
          </TabsContent>
          <TabsContent value="templates">
            <TemplatesTable status="" onReview={setReviewId} refreshKey={refreshKey} onChanged={refresh} />
          </TabsContent>
          <TabsContent value="settings">
            <div className="max-w-md rounded-xl border border-ink/10 bg-white p-5 grid gap-3">
              <Label htmlFor="ap-rate">Default commission %</Label>
              <p className="text-sm text-ink/60 -mt-2">Azamein&rsquo;s share of each partner sale, unless the partner has its own rate. Applies to new sales.</p>
              <div className="flex gap-2">
                <Input id="ap-rate" type="number" min="0" max="100" step="0.5" className="w-32" value={rate} onChange={(e) => setRate(e.target.value)} />
                <Button onClick={saveRate} className="bg-ink text-paper">Save</Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <ReviewDialog templateId={reviewId} onClose={() => setReviewId(null)} onDone={() => { setReviewId(null); refresh(); }} />
      <PartnerSheet partnerId={partnerId} onClose={() => setPartnerId(null)} onChanged={refresh} />
    </div>
  );
}
